import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import InstagramVerifiedBadge from '@src/svgs/InstagramVerifiedBadge';
import MusicNoteIcon from '@src/svgs/MusicNoteIcon';
import SpeakerIcon from '@src/svgs/SpeakerIcon';
import SpeakerMutedIcon from '@src/svgs/SpeakerMutedIcon';
import React from 'react';
import styled from 'styled-components';

import { BrandHandle, BrandLogo } from '../BrandSlots';
import { useChromeFontSize } from '../useChromeFontSize';
import { OverlayFrame, OverlayMuteButton, useMuteToggle } from './sharedChrome';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.instagramFeedPost;

/**
 * Instagram Feed Post overlay — paints what an Instagram viewer sees when a
 * post scrolls into view. Modern Instagram has pulled the post chrome onto
 * the video itself (away from the surrounding feed card), so the overlay
 * paints three pieces of chrome over the rect:
 *
 * 1. **Top profile block** (top-left → top-right): avatar + handle + verified
 *    tick + `·` + time-ago on row one, music-note + audio attribution on row
 *    two, with a white **Follow** pill and an ellipsis pushed to the right
 *    edge. Same posture as Instagram Reels' top header.
 * 2. **Bottom-left tagged-accounts glyph**: small white-outlined person
 *    silhouette inside a translucent circle. Reads as Instagram's
 *    "tagged accounts" affordance. Inlined SVG since it's a one-off
 *    surface-specific glyph — not worth a new file under `src/svgs/`.
 * 3. **Bottom-right mute toggle**: speaker glyph that flips between
 *    `SpeakerMutedIcon` (default) and `SpeakerIcon` when the user toggles —
 *    same `useMuteToggle` hook every overlay uses. The icon swap is the
 *    full muted-state signal; the standard `OverlayMuteButton` opacity dim
 *    is suppressed via `$mutedOpacity={1}` since two cues (dim + swap)
 *    would read as a broken state rather than a deliberate one.
 *
 * `<BrandHandle />` is rendered twice on purpose — once as the account name
 * and once inside the audio-attribution row (`<handle> · Original audio`).
 * Instagram's real surface attributes the audio to the post author when no
 * other source is tagged, which is the showcase-Reels common case here.
 * Don't "deduplicate" — the doubling is the design.
 *
 * No captions block — Instagram doesn't paint a viewer-side captions toggle
 * on feed-post videos. No bottom action row (like / comment / share /
 * bookmark) — those affordances live in the surrounding feed UI, not on the
 * video. No description preview — that lives below the video card.
 *
 * `showPlayControls` gates the entire chrome (top block + tagged-accounts
 * glyph + mute toggle). With the variant off, only the raw video plays.
 *
 * Scaling: chrome is sized in `em` against a base font-size derived from the
 * rect. Reference rect 480×480 → 1em = 10px. Scale factor `min(rect.width /
 * 480, rect.height / 480)` clamped to `[0.5, 3]`.
 */
const REFERENCE_SIZE = 480;
const REFERENCE_FONT_PX = 10;
const SCALE_MIN = 0.5;
const SCALE_MAX = 3;

export const InstagramFeedPostOverlay: React.FC<SafeZoneProps> = ({
  rect,
  variants,
}) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_SIZE,
    referenceHeight: REFERENCE_SIZE,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_MIN,
    scaleMax: SCALE_MAX,
  });

  const showControls = !!variants.showPlayControls;

  const { muted, toggle } = useMuteToggle();

  return (
    <OverlayFrame
      data-testid="instagram-feed-post-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      {showControls && (
        <TopProfileBlock>
          <Avatar>
            <BrandLogo />
          </Avatar>
          <BrandTextStack>
            <NameLine>
              <NameText>
                <BrandHandle />
              </NameText>
              <VerifiedTick aria-label={COPY.aria.verified}>
                <InstagramVerifiedBadge width="1.2em" height="1.2em" />
              </VerifiedTick>
              <NameSeparator aria-hidden="true">·</NameSeparator>
              <TimeAgo>{COPY.timeAgoFallback}</TimeAgo>
            </NameLine>
            <AudioLine>
              <AudioIconWrap>
                <MusicNoteIcon width="1em" height="1em" fillColor="#ffffff" />
              </AudioIconWrap>
              <AudioText>
                <BrandHandle /> · {COPY.audioFallback}
              </AudioText>
            </AudioLine>
          </BrandTextStack>
          <FollowPill type="button" aria-label={COPY.aria.follow}>
            {COPY.followLabel}
          </FollowPill>
          <MoreButton type="button" aria-label={COPY.aria.more}>
            <EllipsisIcon width="1.6em" height="0.4em" fillColor="#ffffff" />
          </MoreButton>
        </TopProfileBlock>
      )}

      {showControls && (
        // Tagged-accounts glyph in the bottom-left. Surface-specific
        // affordance Instagram paints when a post tags one or more accounts
        // — inline SVG since it's a one-off (same justification as the
        // Reels overlay's friends-cluster and preferences glyphs).
        <TaggedAccountsBtn type="button" aria-label={COPY.aria.taggedAccounts}>
          <svg width="1.4em" height="1.4em" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="6" r="2.4" fill="currentColor" />
            <path
              d="M3.6 13.4c.6-2.2 2.4-3.6 4.4-3.6s3.8 1.4 4.4 3.6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </TaggedAccountsBtn>
      )}

      {showControls && (
        <MuteButton
          type="button"
          onClick={toggle}
          aria-label={muted ? COPY.aria.unmute : COPY.aria.mute}
          aria-pressed={muted}
          $muted={muted}
          $mutedOpacity={1}
        >
          {muted ? (
            <SpeakerMutedIcon
              width="1.4em"
              height="1.4em"
              fillColor="#ffffff"
              strokeColor="#ffffff"
            />
          ) : (
            <SpeakerIcon
              width="1.4em"
              height="1.4em"
              fillColor="#ffffff"
              strokeColor="#ffffff"
            />
          )}
        </MuteButton>
      )}
    </OverlayFrame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 480×480 reference).
// ---------------------------------------------------------------------------

// Top block — avatar + name/audio stack + Follow + more. Two-row layout via
// the inner `BrandTextStack`. Soft top-to-bottom gradient so light video
// frames don't bleach the chrome.
const TopProfileBlock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 0.8em 1em;
  display: flex;
  align-items: center;
  gap: 0.8em;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

// Avatar — circular logo with a thin white inset ring. Instagram's
// multi-stop gradient ring approximated as a single solid ring at this
// scale; the simplified version reads cleanly without painting a real
// gradient (which Instagram only shows on accounts that posted a story).
const Avatar = styled.div`
  flex: 0 0 auto;
  width: 2.8em;
  height: 2.8em;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 0.08em rgba(255, 255, 255, 0.9) inset;
`;

// Two-row text stack between the avatar and the right-edge pill / ellipsis.
// `min-width: 0` lets the inner ellipsis clipping work.
const BrandTextStack = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.15em;
`;

// Row one: name + verified tick + `·` + time-ago. Items align on baseline
// with a small gap; the name slot ellipsizes if the username is long.
const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35em;
  min-width: 0;
`;

const NameText = styled.span`
  display: block;
  font-size: 1.2em;
  font-weight: 700;
  letter-spacing: 0.005em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VerifiedTick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

// Static dim grey separator dot — sits between the verified tick and the
// time-ago text. `aria-hidden` keeps it out of the AT tree.
const NameSeparator = styled.span`
  flex: 0 0 auto;
  font-size: 1.1em;
  font-weight: 600;
  opacity: 0.85;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

const TimeAgo = styled.span`
  flex: 0 0 auto;
  font-size: 1.1em;
  font-weight: 500;
  opacity: 0.85;
  letter-spacing: 0.005em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

// Row two: music-note glyph + `<handle> · Original audio`. One-line
// ellipsized. Slightly dimmer than the name row to mirror Instagram's
// actual hierarchy.
const AudioLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  min-width: 0;
`;

const AudioIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

const AudioText = styled.span`
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 1em;
  font-weight: 500;
  opacity: 0.9;
  letter-spacing: 0.005em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// White-fill Follow pill — distinct from Reels' transparent + white-outline
// pill so the two surfaces are recognizable at a glance. Dark text on
// white background mirrors Instagram's actual in-card pill.
const FollowPill = styled.button`
  pointer-events: auto;
  flex: 0 0 auto;
  font-size: 1em;
  font-weight: 700;
  letter-spacing: 0.005em;
  padding: 0.45em 0.95em;
  border: none;
  border-radius: 0.6em;
  background: rgba(255, 255, 255, 0.95);
  color: #1a1a1a;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: #ffffff;
  }
`;

// Ellipsis lives to the right of Follow. Dedicated style instead of reusing
// an action button so the right-edge spacing reads as "two adjacent
// affordances" rather than "icon at end of cluster."
const MoreButton = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4em;
  height: 1.4em;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
  flex: 0 0 auto;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

// Bottom-left tagged-accounts glyph. Circular dark backdrop matches
// Instagram's actual affordance on the live surface — same shape as the
// mute button on the opposite corner so the two read as a pair.
const TaggedAccountsBtn = styled.button`
  pointer-events: auto;
  position: absolute;
  left: 1em;
  bottom: 1em;
  width: 2.4em;
  height: 2.4em;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

// Mute toggle floats in the bottom-right of the rect. `bottom: 1em` so it
// pairs with the tagged-accounts glyph on the opposite corner at the same
// inset distance.
const MuteButton = styled(OverlayMuteButton)`
  right: 1em;
  bottom: 1em;
  width: 2.4em;
  height: 2.4em;
`;
